import { useEffect, useState } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { hasSubmittedLead } from "@/hooks/useLeads";
import { isAdminEnabled } from "@/config/env";
import { useUserAuth } from "@/contexts/UserAuthContext";

interface UserAuthGuardProps {
  children: React.ReactNode;
}

/**
 * UserAuthGuard - Controls access based on signed-in user OR lead submission (Option B).
 *
 * Rules:
 * - /login, /signup, /onboarding, /forgot-password are ALWAYS accessible.
 * - /admin/* routes are accessible when VITE_ENABLE_ADMIN=true (admin protection is separate).
 * - All other routes require: signed-in user (UserAuthContext) OR hasSubmittedLead().
 * - If neither, redirect to /login.
 */
export function UserAuthGuard({ children }: UserAuthGuardProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isLoading } = useUserAuth();
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const alwaysExcludedRoutes = [
      "/onboarding",
      "/login",
      "/signup",
      "/forgot-password",
      "/verify-email",
      "/email-action",
    ];
    const adminExcludedRoutes = isAdminEnabled() ? ["/admin"] : [];
    const excludedRoutes = [...alwaysExcludedRoutes, ...adminExcludedRoutes];

    const isExcluded = excludedRoutes.some((route) =>
      location.pathname.startsWith(route)
    );

    if (import.meta.env.DEV) {
      console.log("[UserAuthGuard]", {
        pathname: location.pathname,
        hasUser: !!user,
        hasSubmittedLead: hasSubmittedLead(),
        isExcluded,
      });
    }

    if (isExcluded) {
      setIsChecking(false);
      return;
    }

    if (isLoading) {
      return;
    }

    // If the user is signed in, check verification based on sign-in method.
    if (user) {
      // Phone-only users (no email) are verified by OTP — allow through.
      if (!user.email) {
        setIsChecking(false);
        return;
      }

      // Email/password users must verify their email.
      if (user.emailVerified) {
        setIsChecking(false);
        return;
      }

      navigate("/verify-email", { replace: true });
      return;
    }

    // Signed-out users can access only if they already submitted a lead.
    const hasLead = hasSubmittedLead();
    if (!hasLead) {
      navigate("/login", { replace: true });
      return;
    }

    setIsChecking(false);
    return;
  }, [location.pathname, navigate, user, isLoading]);

  if (isChecking || isLoading) {
    return (
      <div className="min-h-screen w-full bg-[#f9fafb] flex items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-[#6b7280]">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#e5e7eb] border-t-[#facc15]" />
          <p className="text-sm">جاري تحميل التطبيق...</p>
        </div>
      </div>
    );
  }

  const alwaysExcluded = [
    "/onboarding",
    "/login",
    "/signup",
    "/forgot-password",
    "/verify-email",
    "/email-action",
  ].some((r) => location.pathname.startsWith(r));
  const adminExcluded =
    isAdminEnabled() && location.pathname.startsWith("/admin");
  const allowedWithoutAccess = alwaysExcluded || adminExcluded;
  const hasLead = hasSubmittedLead();
  // Phone users (no email) are verified via OTP; email users need emailVerified.
  const hasAccess = user !== null ? (!user.email || user.emailVerified) : hasLead;

  if (!allowedWithoutAccess && !hasAccess) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}
